import { theme } from '@nicejob-library/design-system';
import {
    AuthCard,
    AuthContainer,
    AuthContent,
    AuthError,
    AuthFooter,
    AuthLoading,
    LoginForm,
} from '@nicejob-library/design-system/authentication';
import { Trans, translate } from '@nicejob-library/internationalization';
import { Paths } from '@nicejob-library/paths';
import React, { ReactElement, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { parseQuery } from '../../../../../../common/parseQuery';
import { IAtomLoginPageProps } from '../../../../types/login.types';
import { ForgotPasswordLink } from '../../components/common/ForgotPasswordLink';
import { HeaderLogo } from '../../components/common/HeaderLogo';
import {
    AccountNotFoundAlert,
    AccountNotFoundBody,
    AccountNotFoundBodyText,
} from '../Login.styles';
import { AtomLoginText, AtomMessage } from './AtomLogin.styles';
import { ResetPasswordLink } from './ResetPassword';

export function AtomLoginPage(props: IAtomLoginPageProps): ReactElement {
    return (
        <ThemeProvider theme={theme}>
            <AtomLoginPageContent {...props} />
        </ThemeProvider>
    );
}

function AtomLoginPageContent(props: IAtomLoginPageProps): ReactElement {
    const [is_loading, setIsLoading] = useState(false);
    const [show_account_not_found_alert, setShowAccountNotFoundAlert] = useState(false);
    const [show_login_hint, setShowLoginHint] = useState(false);

    /**
     * Set loading state when submitting the form
     */
    function onLogin(): void {
        setIsLoading(true);
    }

    const { post_url, email, branding } = props;
    const { brand_name } = branding;

    // Parse Query string to get possible error state.
    const url_query = typeof window !== 'undefined' ? window.location.search : '';

    // get Params
    const params = parseQuery(url_query);

    useEffect(() => {
        if (params.account_not_found) {
            setShowAccountNotFoundAlert(true);
        } else {
            setShowAccountNotFoundAlert(false);
        }

        if (params.show_login_hint) {
            setShowLoginHint(true);
        } else {
            setShowLoginHint(false);
        }
    }, [params]);

    return (
        <AuthContainer>
            <HeaderLogo branding={branding} />

            {is_loading && <AuthLoading />}

            <AuthContent is_loading={is_loading}>
                <AccountNotFoundAlert
                    message={translate({
                        namespace: 'login',
                        key: 'login.error.email_not_found',
                        language: props.language,
                    })}
                    show_alert={show_account_not_found_alert}
                    variant='confirmation'
                    renderAlertBody={(): ReactElement => (
                        <AccountNotFoundBody>
                            <AccountNotFoundBodyText>
                                <b>
                                    {translate({
                                        namespace: 'login',
                                        key: 'login.error.email_not_found',
                                        language: props.language,
                                    })}
                                    .
                                </b>
                            </AccountNotFoundBodyText>
                            <AccountNotFoundBodyText>
                                <Trans
                                    namespace='login'
                                    k='login.account_not_found_solution'
                                    components={{
                                        link: <a href={`${Paths.ONBOARDING_BASE}`} />,
                                    }}
                                />
                            </AccountNotFoundBodyText>
                        </AccountNotFoundBody>
                    )}
                />
                <AuthCard>
                    <AtomLoginText>
                        {translate({
                            namespace: 'login',
                            key: 'login.atom_login_text',
                            language: props.language,
                        })}
                    </AtomLoginText>

                    <LoginForm
                        brand_name={brand_name}
                        post_url={post_url}
                        email={email}
                        onSubmit={onLogin}
                        language={props.language}
                    />

                    <AuthError
                        error={props.error_message}
                        error_prefix={translate({
                            namespace: 'login',
                            key: 'login.error.prefix',
                            language: props.language,
                        })}
                    />

                    {show_login_hint && (
                        <AtomMessage>
                            {translate({
                                namespace: 'login',
                                key: 'login.error.show_login_hint',
                                language: props.language,
                            })}
                        </AtomMessage>
                    )}

                    {props.existing_platforms && (
                        <AtomMessage>
                            {translate({
                                namespace: 'login',
                                key: 'login.atom_onboard_existing_user_message',
                                language: props.language,
                                params: {
                                    existing_platforms: props.existing_platforms,
                                },
                            })}
                        </AtomMessage>
                    )}
                </AuthCard>

                <AuthFooter>
                    {!is_loading &&
                        (params.atom_onboarding_existing_user || params.show_login_hint ? (
                            <ResetPasswordLink />
                        ) : (
                            <ForgotPasswordLink />
                        ))}
                </AuthFooter>
            </AuthContent>
        </AuthContainer>
    );
}
